import React, { useRef, useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { IMonitor } from '../../interfaces/monitor';
import ApiClient from '../../core/apiClient';
import MeStore from '../../mobx/me';
import MonitorStore from '../../mobx/monitors';
import Alert from '../Alert';
import AgentSetup from './AgentSetup';
import AgentSecretDialog from './AgentSecretDialog';

const AgentSecret = ({ monitor, editing = false }: { monitor: IMonitor; editing?: boolean }) => {
  const { t } = useTranslation();
  const [secret, setSecret] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const active = useRef(true);
  useEffect(() => () => { active.current = false; }, []);

  const issue = async () => {
    if (pending.current) return;
    pending.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await ApiClient.issueAgentSecret(monitor.id);
      if (!active.current) return;
      if (!res?.data?.agent_secret) {
        setError(t('AGENT_ISSUE_ERROR'));
        return;
      }
      setConfirm(false);
      setSecret(res.data.agent_secret);
      // Refresh monitor data without putting the one-time secret in the store.
      MonitorStore.getMonitor(monitor.id).catch(() => {});
    } catch {
      if (active.current) setError(t('AGENT_ISSUE_ERROR'));
    } finally {
      pending.current = false;
      if (active.current) setLoading(false);
    }
  };

  if (!editing && monitor.has_agent_secret && !monitor.agent_auth_error && !secret && !confirm) return null;

  return (
    <div className="agent-secret-panel">
      {monitor.agent_auth_error && <Alert type="warning" title={t('AGENT_AUTH_ERROR')} description={t('AGENT_AUTH_HELP')} />}
      <h3 className="t-headline-4">{t('AGENT_SECRET')}</h3>
      <AgentSetup hostKey={monitor.key} owner={monitor.user_id === MeStore.me?.id} />
      {monitor.user_id === MeStore.me?.id && (
        <Button onClick={() => { setError(''); setConfirm(true); }}>{t('AGENT_ISSUE')}</Button>
      )}
      <Modal
        visible={confirm}
        title={t('AGENT_ISSUE')}
        onOk={issue}
        onCancel={() => { if (!pending.current) setConfirm(false); }}
        confirmLoading={loading}
        okText={t('AGENT_CONFIRM')}
        cancelText={t('AGENT_CANCEL')}
        maskClosable={false}
        closable={!loading}
        keyboard={!loading}
        cancelButtonProps={{ disabled: loading }}
      >
        <p>{t('AGENT_ROTATE_WARNING')}</p>
        <p>{t('AGENT_UPGRADE')}</p>
        {error && <p role="alert">{error}</p>}
      </Modal>
      {secret && <AgentSecretDialog hostKey={monitor.key} secret={secret} onClose={() => setSecret('')} />}
    </div>
  );
};

export default AgentSecret;
